import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useAuth } from '../App';
import {
  LayoutDashboard,
  Package,
  GitBranch,
  TrendingUp,
  FolderOpen,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  MapPin,
  Database,
  Archive,
  Building,
  Building2,
  BookOpen,
  FileText,
  Truck,
  Users,
  ShoppingCart,
  ShoppingBag,
  PackageCheck,
  BarChart3,
  Settings2,
  Calculator,
  DollarSign,
  PieChart,
  CreditCard,
  AlertTriangle,
  Shield,
  ClipboardList,
  ClipboardCheck,
  Factory,
  Layers,
  History,
  Crown,
  UserPlus,
  Route,
  Cog,
  Clock,
  Mail,
  Calendar,
  TrendingDown,
  RefreshCw,
  Brain,
  RotateCcw,
  Wallet,
  ArrowRightLeft
} from 'lucide-react';

const Sidebar = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [expandedCategories, setExpandedCategories] = useState(['Dashboard']);
  const [menuCategories, setMenuCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const isMasterAdmin = user && user.role === 'master_admin';

  // Default fallback menu categories (used if no config found)
  const defaultMenuCategories = [
    {
      name: 'Purchasing',
      icon: FileText,
      items: [
        { title: 'Purchase Orders', path: '/purchase-orders', icon: FileText },
        { title: 'Suppliers', path: '/suppliers', icon: Building }
      ]
    },
    {
      name: 'Inventory',
      icon: Package,
      items: [
        { title: 'Transaction History', path: '/transaction-history', icon: History },
        { title: 'Purchase Receiving', path: '/purchase-receiving', icon: Truck },
        { title: 'Put-Away Tasks', path: '/put-away-tasks', icon: PackageCheck },
        { title: 'Inter-Site Transfers', path: '/transfers', icon: ArrowRightLeft },
        { title: 'Locations', path: '/locations', icon: MapPin },
        { title: 'SubInventories', path: '/subinventories', icon: Database },
        { title: 'Item Locations', path: '/item-locations', icon: Archive },
        { title: 'Inventory by Location', path: '/inventory-by-location', icon: Archive },
        { title: 'Inventory Adjustments', path: '/inventory-adjustments', icon: RefreshCw }
      ]
    },
    {
      name: 'Dashboards',
      icon: BarChart3,
      items: [
        { title: 'Executive Dashboard', path: '/executive-dashboard', icon: TrendingUp },
        { title: 'Planner Dashboard', path: '/planner-dashboard', icon: Calculator },
        { title: 'Buyer Dashboard', path: '/buyer-dashboard', icon: ShoppingCart },
        { title: 'Inventory Dashboard', path: '/inventory-dashboard', icon: Package }
      ]
    },
    {
      name: 'Sales',
      icon: ShoppingBag,
      items: [
        { title: 'Sales Orders', path: '/sales-orders', icon: ShoppingCart },
        { title: 'Customers', path: '/customers', icon: Users },
        { title: 'Projects', path: '/projects', icon: FolderOpen },
        { title: 'Fulfillment', path: '/fulfillment', icon: PackageCheck },
        { title: 'RMA / Returns', path: '/rma', icon: RotateCcw }
      ]
    },
    {
      name: 'Production',
      icon: Factory,
      items: [
        { title: 'Work Orders', path: '/work-orders', icon: ClipboardList },
        { title: 'Shop Floor', path: '/shop-floor', icon: ClipboardCheck },
        { title: 'Shop Floor Scheduling', path: '/shop-floor-scheduling', icon: Calendar },
        { title: 'Production Dashboard', path: '/production-dashboard', icon: BarChart3 },
        { title: 'Work Centers', path: '/work-centers', icon: Factory },
        { title: 'Work Order Types', path: '/work-order-types', icon: Layers },
        { title: 'Operations', path: '/operations', icon: Cog }
      ]
    },
    {
      name: 'Engineering',
      icon: GitBranch,
      items: [
        { title: 'BOM Management', path: '/bom', icon: GitBranch },
        { title: 'Routings', path: '/routings', icon: Route }
      ]
    },
    {
      name: 'Quality',
      icon: Shield,
      items: [
        { title: 'Quality Dashboard', path: '/quality-dashboard', icon: Shield },
        { title: 'Inspections', path: '/inspections', icon: ClipboardList },
        { title: 'Non-Conformances', path: '/non-conformances', icon: AlertTriangle },
        { title: 'QC Inspection Plans', path: '/qc-inspection-plans', icon: ClipboardList },
        { title: 'COA Management', path: '/coa-management', icon: FileText },
        { title: 'Inspection Checkpoints', path: '/inspection-checkpoints', icon: ClipboardCheck },
        { title: 'MRB Management', path: '/mrb', icon: AlertTriangle }
      ]
    },
    {
      name: 'Planning',
      icon: Calculator,
      items: [
        { title: 'Item Master', path: '/items', icon: Package },
        { title: 'MRP Dashboard', path: '/mrp-dashboard', icon: Calculator },
        { title: 'Planner Codes', path: '/planner-codes', icon: Settings2 }
      ]
    },
    {
      name: 'Finance',
      icon: DollarSign,
      items: [
        { title: 'Finance Dashboard', path: '/finance-dashboard', icon: PieChart },
        { title: 'Cash Management', path: '/cash-management', icon: Wallet },
        { title: 'Financial Reporting', path: '/financial-reporting', icon: FileText },
        { title: 'Fixed Assets', path: '/fixed-assets', icon: Building2 },
        { title: 'Standard Costs', path: '/standard-costs', icon: DollarSign },
        { title: 'Work Center Costs', path: '/work-center-costs', icon: Factory },
        { title: 'Cost Variances', path: '/cost-variances', icon: TrendingDown },
        { title: 'Chart of Accounts', path: '/chart-of-accounts', icon: DollarSign },
        { title: 'Cross-Validation Rules', path: '/cross-validation-rules', icon: Shield },
        { title: 'AP Dashboard', path: '/ap-dashboard', icon: PieChart },
        { title: 'Supplier Invoices', path: '/supplier-invoices', icon: FileText },
        { title: 'Accounts Receivable', path: '/accounts-receivable', icon: FileText },
        { title: 'Cost Centers', path: '/cost-centers', icon: Building }
      ]
    },
    {
      name: 'Premium Modules',
      icon: Crown,
      items: [
        { title: 'Advanced WMS', path: '/wms', icon: Truck, premium: true },
        { title: 'APS Dashboard', path: '/aps-dashboard', icon: Brain, premium: true }
      ]
    },
    {
      name: 'Administration',
      icon: Settings,
      masterAdminOnly: true,
      items: [
        { title: 'Master Admin', path: '/master-admin', icon: Crown },
        { title: 'E2E Testing', path: '/e2e-testing', icon: ClipboardCheck },
        { title: 'Admin Migrations', path: '/admin-migrations', icon: RefreshCw },
        { title: 'Fix Variance Data', path: '/admin-variance-correction', icon: RefreshCw },
        { title: 'User Management', path: '/user-assignments', icon: Users },
        { title: 'Email Debug', path: '/email-debug', icon: Mail },
        { title: 'Menu Configuration', path: '/menu-configuration', icon: Settings2 },
        { title: 'MRP Scheduling', path: '/mrp-scheduling', icon: Clock },
        { title: 'Location Manager', path: '/inventory-location-manager', icon: Settings2 },
        { title: 'SaaS Customers', path: '/saas-customers', icon: Building2 },
        { title: 'Customer Onboarding', path: '/customer-onboarding', icon: UserPlus },
        { title: 'SaaS Metrics', path: '/saas-metrics', icon: BarChart3 },
        { title: 'Sites Management', path: '/sites', icon: Building2 },
        { title: 'Backup Management', path: '/backups', icon: Database }
      ]
    }
  ];

  // Load menu configuration from backend
  useEffect(() => {
    const loadMenuConfig = async () => {
      if (!user || !user.site_id) {
        setMenuCategories(defaultMenuCategories);
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menu-config/${user.site_id}`, { headers });
        
        if (response.ok) {
          const config = await response.json();
          if (config && Array.isArray(config) && config.length > 0) {
            // Use custom config if available
            setMenuCategories(config);
          } else {
            // Fall back to default
            setMenuCategories(defaultMenuCategories);
          }
        } else {
          // Fall back to default if API fails
          setMenuCategories(defaultMenuCategories);
        }
      } catch (error) {
        console.error('Failed to load menu config:', error);
        setMenuCategories(defaultMenuCategories);
      } finally {
        setLoading(false);
      }
    };

    loadMenuConfig();
  }, [user]);

  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => 
      prev.includes(categoryName) 
        ? prev.filter(name => name !== categoryName)
        : [...prev, categoryName]
    );
  };

  const isCategoryExpanded = (categoryName) => {
    return expandedCategories.includes(categoryName);
  };

  const isActiveItem = (path) => {
    return location.pathname === path;
  };

  const filteredCategories = menuCategories.filter(category => {
    if (category.masterAdminOnly && !isMasterAdmin) return false;
    return true;
  });

  return (
    <div className={`${isOpen ? 'w-64' : 'w-20'} bg-gray-900 text-white transition-all duration-300 flex flex-col h-screen`}>
      {/* Header */}
      <div className="p-4 flex items-center justify-between border-b border-gray-700">
        {isOpen && <h2 className="text-xl font-bold">ERP System</h2>}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-2 rounded-lg hover:bg-gray-700 transition-colors"
        >
          {isOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        {loading ? (
          <div className="text-center py-8">
            <p className="text-gray-400 text-sm">Loading menu...</p>
          </div>
        ) : (
          filteredCategories.map((category) => (
            <div key={category.name} className="mb-2">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category.name)}
                className={`w-full flex items-center justify-between px-4 py-3 hover:bg-gray-800 transition-colors ${
                  isCategoryExpanded(category.name) ? 'bg-gray-800' : ''
                }`}
              >
                <div className="flex items-center space-x-3">
                  <category.icon size={20} />
                  {isOpen && <span className="font-medium">{category.name}</span>}
                </div>
                {isOpen && (
                  isCategoryExpanded(category.name) 
                    ? <ChevronUp size={16} /> 
                    : <ChevronDown size={16} />
                )}
              </button>

              {/* Category Items */}
              {isCategoryExpanded(category.name) && (
                <div className={`${isOpen ? 'pl-4' : ''}`}>
                  {category.items.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center space-x-3 px-4 py-2 hover:bg-gray-800 transition-colors ${
                        isActiveItem(item.path) ? 'bg-blue-600' : ''
                      }`}
                    >
                      <item.icon size={18} />
                      {isOpen && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm">{item.title}</span>
                          {item.premium && (
                            <span className="px-1.5 py-0.5 text-[10px] font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded">
                              PRO
                            </span>
                          )}
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </nav>

      {/* Footer - Logout */}
      <div className="p-4 border-t border-gray-700">
        <button
          onClick={logout}
          className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-800 rounded-lg transition-colors text-red-400"
        >
          <LogOut size={20} />
          {isOpen && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
